/**
 * The panel header's Add Player / Add Team Official button has three states,
 * not two: while the roster is loading, `canAddPlayer`/`canAddOfficial` are
 * both `false` (same as "denied") but the correct render is a skeleton, not
 * nothing — showing nothing on load and popping the real button in later is
 * exactly the layout jump this exists to avoid. Once loaded, `false` means
 * denied and renders nothing; `true` renders the real button.
 */
export type AddButtonState = 'skeleton' | 'button' | 'none'

export interface ResolveAddButtonStateArgs {
  /** Whether the currently visible roster tab is Players or Officials. */
  rosterTab: 'players' | 'officials'
  /** True while the roster data backing the capability check is in flight. */
  isLoading: boolean
  canAddPlayer: boolean
  canAddOfficial: boolean
}

/**
 * Picks the Add button's render state for the visible roster tab.
 *
 * `isLoading` always wins — a team the user cannot edit must still show a
 * skeleton while the answer is unknown, then resolve to 'none' once loaded,
 * never the reverse (a skeleton must never survive past loading).
 */
export const resolveAddButtonState = ({
  rosterTab,
  isLoading,
  canAddPlayer,
  canAddOfficial,
}: ResolveAddButtonStateArgs): AddButtonState => {
  if (isLoading) {
    return 'skeleton'
  }

  const canAdd = rosterTab === 'officials' ? canAddOfficial : canAddPlayer
  return canAdd ? 'button' : 'none'
}
